import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Checkbox,
  ListItemIcon,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Monitor from "@mui/icons-material/Monitor";
import CampaignIcon from "@mui/icons-material/Campaign";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  fetchAdvertisers,
  createAdvertiser,
  updateAdvertiser,
  deleteAdvertiser,
  fetchContractsByAdvertiser,
  createContract,
  updateContract,
  deleteContract,
  fetchCampaignsByContract,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  fetchCampaignPlayers,
  setCampaignPlayers,
  fetchCampaignAds,
  setCampaignAds,
  fetchCustomers,
  fetchDayParts,
  getMediaBlobUrl,
} from "../api/api";
import type { Advertiser, AdContract, Campaign, CampaignPlayerOption, CampaignAdOption, CustomerDayPart } from "../api/api";
import { useTranslation } from "react-i18next";

type CustomerOption = { id: string; name: string };

const CONTRACT_STATUS_OPTIONS: { value: string; i18nKey: string }[] = [
  { value: "draft", i18nKey: "advertiserAdmin.contractStatusDraft" },
  { value: "signed", i18nKey: "advertiserAdmin.contractStatusSigned" },
  { value: "terminated", i18nKey: "advertiserAdmin.contractStatusTerminated" },
];

const CAMPAIGN_STATUS_OPTIONS: { value: string; i18nKey: string }[] = [
  { value: "draft", i18nKey: "advertiserAdmin.statusDraft" },
  { value: "active", i18nKey: "advertiserAdmin.statusActive" },
  { value: "paused", i18nKey: "advertiserAdmin.statusPaused" },
  { value: "completed", i18nKey: "advertiserAdmin.statusCompleted" },
];

function formatCampaignDate(d: string | null | undefined): string {
  if (d == null || d === "") return "—";
  const s = String(d);
  if (s.length < 10) return s;
  const y = s.slice(0, 4);
  const m = s.slice(5, 7);
  const day = s.slice(8, 10);
  return `${day}.${m}.${y}`;
}

const AdvertiserAdminPanel: React.FC = () => {
  const { t } = useTranslation();
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState<Advertiser | null>(null);
  const [contracts, setContracts] = useState<AdContract[]>([]);
  const [selectedContract, setSelectedContract] = useState<AdContract | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Advertiser dialogs
  const [createAdvertiserOpen, setCreateAdvertiserOpen] = useState(false);
  const [newAdvertiserName, setNewAdvertiserName] = useState("");
  const [newAdvertiserIndustry, setNewAdvertiserIndustry] = useState("");
  const [editAdvertiserOpen, setEditAdvertiserOpen] = useState(false);
  const [editAdvertiser, setEditAdvertiser] = useState<Advertiser | null>(null);
  const [editAdvertiserName, setEditAdvertiserName] = useState("");
  const [editAdvertiserIndustry, setEditAdvertiserIndustry] = useState("");
  const [deleteAdvertiserOpen, setDeleteAdvertiserOpen] = useState(false);
  const [advertiserToDelete, setAdvertiserToDelete] = useState<Advertiser | null>(null);

  // Contract dialogs
  const [createContractOpen, setCreateContractOpen] = useState(false);
  const [newContractName, setNewContractName] = useState("");
  const [newContractCustomerId, setNewContractCustomerId] = useState("");
  const [newContractStatus, setNewContractStatus] = useState<string>("draft");
  const [newContractPricingModel, setNewContractPricingModel] = useState("");
  const [newContractCurrency, setNewContractCurrency] = useState("");
  const [newContractBillingPeriod, setNewContractBillingPeriod] = useState("");
  const [newContractRevenueShare, setNewContractRevenueShare] = useState("");
  const [newContractContactPerson, setNewContractContactPerson] = useState("");
  const [newContractLanguage, setNewContractLanguage] = useState("");
  const [editContractOpen, setEditContractOpen] = useState(false);
  const [contractToEdit, setContractToEdit] = useState<AdContract | null>(null);
  const [editContractName, setEditContractName] = useState("");
  const [editContractCustomerId, setEditContractCustomerId] = useState("");
  const [editContractStatus, setEditContractStatus] = useState("");
  const [editContractPricingModel, setEditContractPricingModel] = useState("");
  const [editContractCurrency, setEditContractCurrency] = useState("");
  const [editContractBillingPeriod, setEditContractBillingPeriod] = useState("");
  const [editContractRevenueShare, setEditContractRevenueShare] = useState("");
  const [editContractContactPerson, setEditContractContactPerson] = useState("");
  const [editContractLanguage, setEditContractLanguage] = useState("");
  const [deleteContractOpen, setDeleteContractOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<AdContract | null>(null);

  // Campaign dialogs
  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState("");
  const [newCampaignStartDate, setNewCampaignStartDate] = useState("");
  const [newCampaignEndDate, setNewCampaignEndDate] = useState("");
  const [newCampaignPlayGoal, setNewCampaignPlayGoal] = useState("");
  const [newCampaignDayparting, setNewCampaignDayparting] = useState("");
  const [newCampaignStatus, setNewCampaignStatus] = useState<string>("active");
  const [editCampaignOpen, setEditCampaignOpen] = useState(false);
  const [campaignToEdit, setCampaignToEdit] = useState<Campaign | null>(null);
  const [editCampaignName, setEditCampaignName] = useState("");
  const [editCampaignStartDate, setEditCampaignStartDate] = useState("");
  const [editCampaignEndDate, setEditCampaignEndDate] = useState("");
  const [editCampaignPlayGoal, setEditCampaignPlayGoal] = useState("");
  const [editCampaignDayparting, setEditCampaignDayparting] = useState("");
  const [editCampaignStatus, setEditCampaignStatus] = useState("");
  const [deleteCampaignOpen, setDeleteCampaignOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);
  const [contractDayParts, setContractDayParts] = useState<CustomerDayPart[]>([]);

  // Campaign players dialog
  const [selectPlayersOpen, setSelectPlayersOpen] = useState(false);
  const [campaignForPlayers, setCampaignForPlayers] = useState<Campaign | null>(null);
  const [playersList, setPlayersList] = useState<CampaignPlayerOption[]>([]);
  const [assignedPlayerSns, setAssignedPlayerSns] = useState<string[]>([]);

  // Campaign ads dialog
  const [selectAdsOpen, setSelectAdsOpen] = useState(false);
  const [campaignForAds, setCampaignForAds] = useState<Campaign | null>(null);
  const [assignedAds, setAssignedAds] = useState<{ ad_id: number; weight?: number; play_goal_override?: string | null }[]>([]);
  const [availableAds, setAvailableAds] = useState<CampaignAdOption[]>([]);
  // Ad-Vorschau (Ads auswählen)
  const [adPreviewOpen, setAdPreviewOpen] = useState(false);
  const [adPreviewUrl, setAdPreviewUrl] = useState<string | null>(null);
  const [adPreviewType, setAdPreviewType] = useState<"video" | "audio" | "image" | null>(null);

  const loadAdvertisers = useCallback(async () => {
    const list = await fetchAdvertisers();
    setAdvertisers(list);
  }, []);

  const loadCustomers = useCallback(async () => {
    const list = await fetchCustomers();
    setCustomers(list);
  }, []);

  const loadContracts = useCallback(async () => {
    if (!selectedAdvertiser) {
      setContracts([]);
      return;
    }
    const list = await fetchContractsByAdvertiser(selectedAdvertiser.id);
    setContracts(list);
  }, [selectedAdvertiser]);

  const loadCampaigns = useCallback(async () => {
    if (!selectedContract) {
      setCampaigns([]);
      return;
    }
    const list = await fetchCampaignsByContract(selectedContract.id);
    setCampaigns(list);
  }, [selectedContract]);

  useEffect(() => {
    loadAdvertisers();
    loadCustomers();
  }, [loadAdvertisers, loadCustomers]);

  useEffect(() => {
    loadContracts();
    setSelectedContract(null);
  }, [selectedAdvertiser, loadContracts]);

  useEffect(() => {
    loadCampaigns();
  }, [selectedContract, loadCampaigns]);

  useEffect(() => {
    if (selectedContract?.customer_id) {
      fetchDayParts(selectedContract.customer_id).then(setContractDayParts).catch(() => setContractDayParts([]));
    } else {
      setContractDayParts([]);
    }
  }, [selectedContract?.customer_id]);

  // ——— Advertiser CRUD ———
  const handleCreateAdvertiser = async () => {
    if (!newAdvertiserName.trim()) return;
    try {
      await createAdvertiser({ name: newAdvertiserName.trim(), industry: newAdvertiserIndustry.trim() || null });
      setSnackbar(t("advertiserAdmin.advertiserCreated"));
      setCreateAdvertiserOpen(false);
      setNewAdvertiserName("");
      setNewAdvertiserIndustry("");
      loadAdvertisers();
    } catch (e) {
      setSnackbar(String(e));
    }
  };

  const openEditAdvertiser = (a: Advertiser) => {
    setEditAdvertiser(a);
    setEditAdvertiserName(a.name);
    setEditAdvertiserIndustry(a.industry ?? "");
    setEditAdvertiserOpen(true);
  };

  const handleSaveEditAdvertiser = async () => {
    if (!editAdvertiser) return;
    try {
      await updateAdvertiser(editAdvertiser.id, {
        name: editAdvertiserName.trim(),
        industry: editAdvertiserIndustry.trim() || null,
      });
      setSnackbar(t("advertiserAdmin.advertiserUpdated"));
      setEditAdvertiserOpen(false);
      setEditAdvertiser(null);
      loadAdvertisers();
      if (selectedAdvertiser?.id === editAdvertiser.id) {
        setSelectedAdvertiser((prev) => (prev ? { ...prev, name: editAdvertiserName.trim(), industry: editAdvertiserIndustry.trim() || null } : null));
      }
    } catch (e) {
      setSnackbar(String(e));
    }
  };

  const openDeleteAdvertiser = (a: Advertiser) => {
    setAdvertiserToDelete(a);
    setDeleteAdvertiserOpen(true);
  };

  const handleConfirmDeleteAdvertiser = async () => {
    if (!advertiserToDelete) return;
    try {
      await deleteAdvertiser(advertiserToDelete.id);
      setSnackbar(t("advertiserAdmin.advertiserDeleted"));
      setDeleteAdvertiserOpen(false);
      setAdvertiserToDelete(null);
      if (selectedAdvertiser?.id === advertiserToDelete.id) setSelectedAdvertiser(null);
      loadAdvertisers();
    } catch (e) {
      setSnackbar(String(e));
    }
  };

  // ——— Contract CRUD ———
  const handleCreateContract = async () => {
    if (!selectedAdvertiser || !newContractName.trim() || !newContractCustomerId || !newContractStatus) return;
    try {
      await createContract(selectedAdvertiser.id, {
        name: newContractName.trim(),
        customer_id: newContractCustomerId,
        status: newContractStatus,
        pricing_model: newContractPricingModel || null,
        currency: newContractCurrency || null,
        billing_period: newContractBillingPeriod || null,
        revenue_share: newContractRevenueShare || null,
        contact_person: newContractContactPerson || null,
        language: newContractLanguage || null,
      });
      setSnackbar(t("advertiserAdmin.contractCreated"));
      setCreateContractOpen(false);
      setNewContractName("");
      setNewContractCustomerId("");
      setNewContractStatus("");
      setNewContractPricingModel("");
      setNewContractCurrency("");
      setNewContractBillingPeriod("");
      setNewContractRevenueShare("");
      setNewContractContactPerson("");
      setNewContractLanguage("");
      loadContracts();
    } catch (e) {
      setSnackbar(String(e));
    }
  };

  const openEditContract = (c: AdContract) => {
    setContractToEdit(c);
    setEditContractName(c.name);
    setEditContractCustomerId(c.customer_id);
    setEditContractStatus(CONTRACT_STATUS_OPTIONS.some((o) => o.value === c.status) ? (c.status as string) : "draft");
    setEditContractPricingModel(c.pricing_model ?? "");
    setEditContractCurrency(c.currency ?? "");
    setEditContractBillingPeriod(c.billing_period ?? "");
    setEditContractRevenueShare(c.revenue_share ?? "");
    setEditContractContactPerson(c.contact_person ?? "");
    setEditContractLanguage(c.language ?? "");
    setEditContractOpen(true);
  };

  const handleSaveEditContract = async () => {
    if (!selectedAdvertiser || !contractToEdit) return;
    try {
      await updateContract(selectedAdvertiser.id, contractToEdit.id, {
        name: editContractName.trim(),
        customer_id: editContractCustomerId,
        status: editContractStatus,
        pricing_model: editContractPricingModel || null,
        currency: editContractCurrency || null,
        billing_period: editContractBillingPeriod || null,
        revenue_share: editContractRevenueShare || null,
        contact_person: editContractContactPerson || null,
        language: editContractLanguage || null,
      });
      setSnackbar(t("advertiserAdmin.contractUpdated"));
      setEditContractOpen(false);
      setContractToEdit(null);
      loadContracts();
      if (selectedContract?.id === contractToEdit.id) {
        setSelectedContract((prev) =>
          prev
            ? {
                ...prev,
                name: editContractName.trim(),
                customer_id: editContractCustomerId,
                customer_name: customers.find((x) => x.id === editContractCustomerId)?.name,
                status: editContractStatus,
                pricing_model: editContractPricingModel || null,
                currency: editContractCurrency || null,
                billing_period: editContractBillingPeriod || null,
                revenue_share: editContractRevenueShare || null,
                contact_person: editContractContactPerson || null,
                language: editContractLanguage || null,
              }
            : null
        );
      }
    } catch (e) {
      setSnackbar(String(e));
    }
  };

  const openDeleteContract = (c: AdContract) => {
    setContractToDelete(c);
    setDeleteContractOpen(true);
  };

  const handleConfirmDeleteContract = async () => {
    if (!selectedAdvertiser || !contractToDelete) return;
    try {
      await deleteContract(selectedAdvertiser.id, contractToDelete.id);
      setSnackbar(t("advertiserAdmin.contractDeleted"));
      setDeleteContractOpen(false);
      if (selectedContract?.id === contractToDelete.id) setSelectedContract(null);
      setContractToDelete(null);
      loadContracts();
    } catch (e) {
      setSnackbar(String(e));
    }
  };

  // ——— Campaign CRUD ———
  const handleCreateCampaign = async () => {
    if (!selectedContract || !newCampaignName.trim() || !newCampaignStartDate.trim() || !newCampaignEndDate.trim() || !newCampaignStatus) return;
    try {
      await createCampaign(selectedContract.id, {
        name: newCampaignName.trim(),
        start_date: newCampaignStartDate.trim(),
        end_date: newCampaignEndDate.trim(),
        play_goal: newCampaignPlayGoal || null,
        dayparting: newCampaignDayparting || null,
        status: newCampaignStatus,
      });
      setSnackbar(t("advertiserAdmin.campaignCreated"));
      setCreateCampaignOpen(false);
      setNewCampaignName("");
      setNewCampaignStartDate("");
      setNewCampaignEndDate("");
      setNewCampaignPlayGoal("");
      setNewCampaignDayparting("");
      setNewCampaignStatus("active");
      loadCampaigns();
    } catch (e) {
      setSnackbar(String(e));
    }
  };

  const openEditCampaign = (c: Campaign) => {
    setCampaignToEdit(c);
    setEditCampaignName(c.name);
    setEditCampaignStartDate(c.start_date ? String(c.start_date).slice(0, 10) : "");
    setEditCampaignEndDate(c.end_date ? String(c.end_date).slice(0, 10) : "");
    setEditCampaignPlayGoal(c.play_goal ?? "");
    setEditCampaignDayparting(c.dayparting ?? "");
    setEditCampaignStatus(CAMPAIGN_STATUS_OPTIONS.some((o) => o.value === c.status) ? (c.status as string) : "active");
    setEditCampaignOpen(true);
  };

  const handleSaveEditCampaign = async () => {
    if (!selectedContract || !campaignToEdit || !editCampaignName.trim() || !editCampaignStartDate.trim() || !editCampaignEndDate.trim() || !editCampaignStatus) return;
    try {
      await updateCampaign(selectedContract.id, campaignToEdit.id, {
        name: editCampaignName.trim(),
        start_date: editCampaignStartDate.trim(),
        end_date: editCampaignEndDate.trim(),
        play_goal: editCampaignPlayGoal || null,
        dayparting: editCampaignDayparting || null,
        status: editCampaignStatus,
      });
      setSnackbar(t("advertiserAdmin.campaignUpdated"));
      setEditCampaignOpen(false);
      setCampaignToEdit(null);
      loadCampaigns();
    } catch (e) {
      setSnackbar(String(e));
    }
  };

  const openDeleteCampaign = (c: Campaign) => {
    setCampaignToDelete(c);
    setDeleteCampaignOpen(true);
  };

  const handleConfirmDeleteCampaign = async () => {
    if (!selectedContract || !campaignToDelete) return;
    try {
      await deleteCampaign(selectedContract.id, campaignToDelete.id);
      setSnackbar(t("advertiserAdmin.campaignDeleted"));
      setDeleteCampaignOpen(false);
      setCampaignToDelete(null);
      loadCampaigns();
    } catch (e) {
      setSnackbar(String(e));
    }
  };

  // ——— Campaign players ———
  const openSelectPlayers = async (campaign: Campaign) => {
    setCampaignForPlayers(campaign);
    const { players, assigned } = await fetchCampaignPlayers(campaign.id);
    setPlayersList(players);
    setAssignedPlayerSns(assigned);
    setSelectPlayersOpen(true);
  };

  const togglePlayer = (sn: string) => {
    setAssignedPlayerSns((prev) =>
      prev.includes(sn) ? prev.filter((s) => s !== sn) : [...prev, sn]
    );
  };

  const handleSaveCampaignPlayers = async () => {
    if (!campaignForPlayers) return;
    try {
      await setCampaignPlayers(campaignForPlayers.id, assignedPlayerSns);
      setSnackbar(t("advertiserAdmin.playersAssigned"));
      setSelectPlayersOpen(false);
      setCampaignForPlayers(null);
    } catch (e) {
      setSnackbar(String(e));
    }
  };

  // ——— Campaign ads ———
  const openSelectAds = async (campaign: Campaign) => {
    setCampaignForAds(campaign);
    const { assigned, available } = await fetchCampaignAds(campaign.id);
    setAssignedAds(assigned);
    setAvailableAds(available);
    setSelectAdsOpen(true);
  };

  const [adsSelection, setAdsSelection] = useState<{ ad_id: number; weight?: number }[]>([]);

  useEffect(() => {
    if (selectAdsOpen) {
      setAdsSelection(assignedAds.map((a) => ({ ad_id: a.ad_id, weight: a.weight })));
    }
  }, [selectAdsOpen, assignedAds]);

  const toggleAd = (adId: number) => {
    setAdsSelection((prev) => {
      const has = prev.some((a) => a.ad_id === adId);
      if (has) return prev.filter((a) => a.ad_id !== adId);
      return [...prev, { ad_id: adId }];
    });
  };

  const handleSaveCampaignAds = async () => {
    if (!campaignForAds) return;
    try {
      await setCampaignAds(
        campaignForAds.id,
        adsSelection.map((a) => ({ ad_id: a.ad_id, weight: a.weight }))
      );
      setSnackbar(t("advertiserAdmin.adsAssigned"));
      setSelectAdsOpen(false);
      setCampaignForAds(null);
    } catch (e) {
      setSnackbar(String(e));
    }
  };

  const handleOpenAdPreview = async (fileName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!fileName) return;
    try {
      const blobUrl = await getMediaBlobUrl(fileName, { dir: "ads" });
      if (blobUrl) {
        const ext = fileName.split(".").pop()?.toLowerCase();
        let type: "video" | "audio" | "image" | null = null;
        if (ext && ["mp4", "webm", "ogg", "avi", "mov", "mkv"].includes(ext)) type = "video";
        else if (ext && ["mp3", "wav", "flac", "aac"].includes(ext)) type = "audio";
        else if (ext && ["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) type = "image";
        setAdPreviewUrl(blobUrl);
        setAdPreviewType(type);
        setAdPreviewOpen(true);
      } else {
        setSnackbar(t("contentConfig.mediaLoadError") || "Fehler beim Laden.");
      }
    } catch (err) {
      setSnackbar(String(err));
    }
  };

  const handleCloseAdPreview = () => {
    setAdPreviewOpen(false);
    if (adPreviewUrl) {
      URL.revokeObjectURL(adPreviewUrl);
      setAdPreviewUrl(null);
    }
    setAdPreviewType(null);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
        {/* Left: Advertisers */}
        <Box sx={{ width: { xs: "100%", md: "33.333%" } }}>
          <Paper sx={{ p: 1.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1">{t("advertiserAdmin.advertisersHeading")}</Typography>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setCreateAdvertiserOpen(true)}>
                {t("advertiserAdmin.createAdvertiserButton")}
              </Button>
            </Box>
            <List>
              {advertisers.length === 0 ? (
                <ListItem>
                  <ListItemText primary={t("advertiserAdmin.noAdvertisers")} sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem" } }} />
                </ListItem>
              ) : (
                advertisers.map((a) => (
                  <ListItem
                    key={a.id}
                    component={ListItemButton}
                    onClick={() => setSelectedAdvertiser(a)}
                    selected={selectedAdvertiser?.id === a.id}
                    secondaryAction={
                      <Box>
                        <IconButton edge="end" onClick={(e) => { e.stopPropagation(); openEditAdvertiser(a); }} title={t("common.edit")} sx={{ mr: 0.5 }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton edge="end" onClick={(e) => { e.stopPropagation(); openDeleteAdvertiser(a); }} title={t("common.delete")} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText primary={a.name} sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem" } }} />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Box>

        {/* Right: Contracts + Campaigns */}
        <Box sx={{ width: { xs: "100%", md: "66.666%" }, display: "flex", flexDirection: "column", gap: 2 }}>
          {selectedAdvertiser && (
            <>
              <Paper sx={{ p: 1.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1">{t("advertiserAdmin.contractsHeading")}</Typography>
                  <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setCreateContractOpen(true)}>
                    {t("advertiserAdmin.createContractButton")}
                  </Button>
                </Box>
                <List dense sx={{ py: 0 }}>
                  {contracts.length === 0 ? (
                    <ListItem>
                      <ListItemText primary={t("advertiserAdmin.noContractsForAdvertiser")} sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem" } }} />
                    </ListItem>
                  ) : (
                    contracts.map((c) => (
                      <ListItem
                        key={c.id}
                        component={ListItemButton}
                        onClick={() => setSelectedContract(c)}
                        selected={selectedContract?.id === c.id}
                        secondaryAction={
                          <Box>
                            <IconButton edge="end" onClick={(e) => { e.stopPropagation(); openEditContract(c); }} title={t("common.edit")} sx={{ mr: 0.5 }}>
                              <EditIcon />
                            </IconButton>
                            <IconButton edge="end" onClick={(e) => { e.stopPropagation(); openDeleteContract(c); }} title={t("common.delete")} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={[c.name, c.status ? ` · ${t(CONTRACT_STATUS_OPTIONS.find((o) => o.value === c.status)?.i18nKey ?? c.status)}` : ""].join("")}
                          secondary={`${selectedAdvertiser?.name ?? ""} - ${c.customer_name ?? c.customer_id}`}
                          sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem" } }}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </Paper>

              {selectedContract && (
                <Paper sx={{ p: 1.5 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle1">{t("advertiserAdmin.campaignsHeading")}</Typography>
                    <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setCreateCampaignOpen(true)}>
                      {t("advertiserAdmin.createCampaignButton")}
                    </Button>
                  </Box>
                  <List dense sx={{ py: 0 }}>
                    {campaigns.length === 0 ? (
                      <ListItem>
                        <ListItemText primary={t("advertiserAdmin.noCampaignsForContract")} sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem" } }} />
                      </ListItem>
                    ) : (
                      campaigns.map((camp) => (
                        <ListItem
                          key={camp.id}
                          secondaryAction={
                            <Box>
                              <IconButton
                                edge="end"
                                onClick={() => openSelectPlayers(camp)}
                                title={t("advertiserAdmin.selectPlayers")}
                                sx={{ mr: 0.5 }}
                              >
                                <Monitor />
                              </IconButton>
                              <IconButton
                                edge="end"
                                onClick={() => openSelectAds(camp)}
                                title={t("advertiserAdmin.selectAds")}
                                sx={{ mr: 0.5 }}
                              >
                                <CampaignIcon />
                              </IconButton>
                              <IconButton edge="end" onClick={() => openEditCampaign(camp)} title={t("common.edit")} sx={{ mr: 0.5 }}>
                                <EditIcon />
                              </IconButton>
                              <IconButton edge="end" onClick={() => openDeleteCampaign(camp)} title={t("common.delete")} color="error">
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemText
                            primary={[camp.name, camp.status ? ` · ${(() => { const opt = CAMPAIGN_STATUS_OPTIONS.find((o) => o.value === camp.status); return opt ? t(opt.i18nKey) : camp.status; })()}` : ""].join("")}
                            secondary={`${formatCampaignDate(camp.start_date)} - ${formatCampaignDate(camp.end_date)}  ·  ${t("advertiserAdmin.playGoalShort")}: ${camp.play_goal ?? "—"}`}
                            sx={{ "& .MuiListItemText-primary": { fontSize: "0.875rem" }, "& .MuiListItemText-secondary": { fontSize: "0.75rem" } }}
                          />
                        </ListItem>
                      ))
                    )}
                  </List>
                </Paper>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Create Advertiser Dialog */}
      <Dialog open={createAdvertiserOpen} onClose={() => setCreateAdvertiserOpen(false)}>
        <DialogTitle>{t("advertiserAdmin.createAdvertiserDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField label={t("advertiserAdmin.advertiserNameLabel")} value={newAdvertiserName} onChange={(e) => setNewAdvertiserName(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <TextField label={t("advertiserAdmin.industryLabel")} value={newAdvertiserIndustry} onChange={(e) => setNewAdvertiserIndustry(e.target.value)} fullWidth sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateAdvertiserOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCreateAdvertiser} disabled={!newAdvertiserName.trim()}>{t("common.create")}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Advertiser Dialog */}
      <Dialog open={editAdvertiserOpen} onClose={() => setEditAdvertiserOpen(false)}>
        <DialogTitle>{t("advertiserAdmin.editAdvertiserDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField label={t("advertiserAdmin.advertiserNameLabel")} value={editAdvertiserName} onChange={(e) => setEditAdvertiserName(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <TextField label={t("advertiserAdmin.industryLabel")} value={editAdvertiserIndustry} onChange={(e) => setEditAdvertiserIndustry(e.target.value)} fullWidth sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAdvertiserOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveEditAdvertiser}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Advertiser Dialog */}
      <Dialog open={deleteAdvertiserOpen} onClose={() => setDeleteAdvertiserOpen(false)}>
        <DialogTitle>{t("customerManagement.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("advertiserAdmin.confirmDeleteAdvertiser", { name: advertiserToDelete?.name })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAdvertiserOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeleteAdvertiser} color="error">{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      {/* Create Contract Dialog */}
      <Dialog open={createContractOpen} onClose={() => setCreateContractOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("advertiserAdmin.createContractDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField label={t("advertiserAdmin.contractNameLabel")} value={newContractName} onChange={(e) => setNewContractName(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t("advertiserAdmin.customerLabel")}</InputLabel>
            <Select value={newContractCustomerId} onChange={(e) => setNewContractCustomerId(e.target.value)} label={t("advertiserAdmin.customerLabel")}>
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>{t("advertiserAdmin.statusLabel")}</InputLabel>
              <Select value={newContractStatus} onChange={(e) => setNewContractStatus(e.target.value)} label={t("advertiserAdmin.statusLabel")}>
                {CONTRACT_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{t(opt.i18nKey)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          <TextField label={t("advertiserAdmin.pricingModelLabel")} value={newContractPricingModel} onChange={(e) => setNewContractPricingModel(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <TextField label={t("advertiserAdmin.currencyLabel")} value={newContractCurrency} onChange={(e) => setNewContractCurrency(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <TextField label={t("advertiserAdmin.billingPeriodLabel")} value={newContractBillingPeriod} onChange={(e) => setNewContractBillingPeriod(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <TextField label={t("advertiserAdmin.revenueShareLabel")} value={newContractRevenueShare} onChange={(e) => setNewContractRevenueShare(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <TextField label={t("advertiserAdmin.contactPersonLabel")} value={newContractContactPerson} onChange={(e) => setNewContractContactPerson(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <TextField label={t("advertiserAdmin.languageLabel")} value={newContractLanguage} onChange={(e) => setNewContractLanguage(e.target.value)} fullWidth sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateContractOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCreateContract} disabled={!newContractName.trim() || !newContractCustomerId || !newContractStatus}>{t("common.create")}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contract Dialog */}
      <Dialog open={editContractOpen} onClose={() => setEditContractOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("advertiserAdmin.editContractDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField label={t("advertiserAdmin.contractNameLabel")} value={editContractName} onChange={(e) => setEditContractName(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <TextField
              label={t("advertiserAdmin.advertiserLabel")}
              value={selectedAdvertiser?.name ?? ""}
              fullWidth
              sx={{
                flex: "1 1 50%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "action.hover",
                  cursor: "default",
                  "& fieldset": { borderColor: "divider" },
                },
              }}
              InputProps={{ readOnly: true }}
            />
            <FormControl sx={{ flex: "1 1 50%" }}>
              <InputLabel>{t("advertiserAdmin.contractPartnerLabel")}</InputLabel>
              <Select value={editContractCustomerId} onChange={(e) => setEditContractCustomerId(e.target.value)} label={t("advertiserAdmin.contractPartnerLabel")}>
                {customers.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t("advertiserAdmin.statusLabel")}</InputLabel>
            <Select value={editContractStatus} onChange={(e) => setEditContractStatus(e.target.value)} label={t("advertiserAdmin.statusLabel")}>
              {CONTRACT_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{t(opt.i18nKey)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>{t("advertiserAdmin.billingSectionLabel")}</Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
            <TextField label={t("advertiserAdmin.pricingModelLabel")} value={editContractPricingModel} onChange={(e) => setEditContractPricingModel(e.target.value)} sx={{ flex: 2, minWidth: 0 }} />
            <TextField label={t("advertiserAdmin.currencyLabel")} value={editContractCurrency} onChange={(e) => setEditContractCurrency(e.target.value)} sx={{ flex: 1, minWidth: 0 }} />
          </Box>
          <TextField label={t("advertiserAdmin.billingPeriodLabel")} value={editContractBillingPeriod} onChange={(e) => setEditContractBillingPeriod(e.target.value)} fullWidth sx={{ mb: 1 }} />
          <TextField label={t("advertiserAdmin.revenueShareLabel")} value={editContractRevenueShare} onChange={(e) => setEditContractRevenueShare(e.target.value)} fullWidth sx={{ mb: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>{t("advertiserAdmin.contactSectionLabel")}</Typography>
          <TextField label={t("advertiserAdmin.contactPersonLabel")} value={editContractContactPerson} onChange={(e) => setEditContractContactPerson(e.target.value)} fullWidth sx={{ mb: 1 }} />
          <TextField label={t("advertiserAdmin.languageLabel")} value={editContractLanguage} onChange={(e) => setEditContractLanguage(e.target.value)} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditContractOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveEditContract} disabled={!editContractName.trim() || !editContractCustomerId || !editContractStatus}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Contract Dialog */}
      <Dialog open={deleteContractOpen} onClose={() => setDeleteContractOpen(false)}>
        <DialogTitle>{t("customerManagement.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("advertiserAdmin.confirmDeleteContract", { name: contractToDelete?.name })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteContractOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeleteContract} color="error">{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      {/* Create Campaign Dialog */}
      <Dialog open={createCampaignOpen} onClose={() => setCreateCampaignOpen(false)}>
        <DialogTitle>{t("advertiserAdmin.createCampaignDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField label={t("advertiserAdmin.campaignNameLabel")} value={newCampaignName} onChange={(e) => setNewCampaignName(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <TextField type="date" label={t("advertiserAdmin.startDateLabel")} value={newCampaignStartDate} onChange={(e) => setNewCampaignStartDate(e.target.value)} fullWidth sx={{ mt: 1 }} InputLabelProps={{ shrink: true }} />
          <TextField type="date" label={t("advertiserAdmin.endDateLabel")} value={newCampaignEndDate} onChange={(e) => setNewCampaignEndDate(e.target.value)} fullWidth sx={{ mt: 1 }} InputLabelProps={{ shrink: true }} />
          <TextField label={t("advertiserAdmin.playGoalLabel")} value={newCampaignPlayGoal} onChange={(e) => setNewCampaignPlayGoal(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t("advertiserAdmin.daypartingLabel")}</InputLabel>
            <Select value={newCampaignDayparting} onChange={(e) => setNewCampaignDayparting(e.target.value)} label={t("advertiserAdmin.daypartingLabel")}>
              <MenuItem value="">—</MenuItem>
              {contractDayParts.map((dp) => (
                <MenuItem key={dp.id} value={dp.name}>{dp.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t("advertiserAdmin.statusLabel")}</InputLabel>
            <Select value={newCampaignStatus} onChange={(e) => setNewCampaignStatus(e.target.value)} label={t("advertiserAdmin.statusLabel")}>
              {CAMPAIGN_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{t(opt.i18nKey)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCampaignOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCreateCampaign} disabled={!newCampaignName.trim() || !newCampaignStartDate.trim() || !newCampaignEndDate.trim() || !newCampaignStatus}>{t("common.create")}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Campaign Dialog */}
      <Dialog open={editCampaignOpen} onClose={() => setEditCampaignOpen(false)}>
        <DialogTitle>{t("advertiserAdmin.editCampaignDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField label={t("advertiserAdmin.campaignNameLabel")} value={editCampaignName} onChange={(e) => setEditCampaignName(e.target.value)} fullWidth sx={{ mt: 1 }} />
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <TextField type="date" label={t("advertiserAdmin.startDateLabel")} value={editCampaignStartDate} onChange={(e) => setEditCampaignStartDate(e.target.value)} sx={{ flex: 1 }} InputLabelProps={{ shrink: true }} />
            <TextField type="date" label={t("advertiserAdmin.endDateLabel")} value={editCampaignEndDate} onChange={(e) => setEditCampaignEndDate(e.target.value)} sx={{ flex: 1 }} InputLabelProps={{ shrink: true }} />
          </Box>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t("advertiserAdmin.statusLabel")}</InputLabel>
            <Select value={editCampaignStatus} onChange={(e) => setEditCampaignStatus(e.target.value)} label={t("advertiserAdmin.statusLabel")}>
              {CAMPAIGN_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{t(opt.i18nKey)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>{t("advertiserAdmin.campaignGoalSectionLabel")}</Typography>
          <TextField label={t("advertiserAdmin.playGoalLabel")} value={editCampaignPlayGoal} onChange={(e) => setEditCampaignPlayGoal(e.target.value)} fullWidth sx={{ mb: 1 }} />
          <FormControl fullWidth>
            <InputLabel>{t("advertiserAdmin.daypartingLabel")}</InputLabel>
            <Select value={editCampaignDayparting} onChange={(e) => setEditCampaignDayparting(e.target.value)} label={t("advertiserAdmin.daypartingLabel")}>
              <MenuItem value="">—</MenuItem>
              {contractDayParts.map((dp) => (
                <MenuItem key={dp.id} value={dp.name}>{dp.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCampaignOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveEditCampaign} disabled={!editCampaignName.trim() || !editCampaignStartDate.trim() || !editCampaignEndDate.trim() || !editCampaignStatus}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Campaign Dialog */}
      <Dialog open={deleteCampaignOpen} onClose={() => setDeleteCampaignOpen(false)}>
        <DialogTitle>{t("customerManagement.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("advertiserAdmin.confirmDeleteCampaign", { name: campaignToDelete?.name })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCampaignOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeleteCampaign} color="error">{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      {/* Select Players Dialog */}
      <Dialog open={selectPlayersOpen} onClose={() => setSelectPlayersOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("advertiserAdmin.selectPlayersDialogTitle")}</DialogTitle>
        <DialogContent>
          <List>
            {playersList.map((p) => (
              <ListItem key={p.sn} dense button onClick={() => togglePlayer(p.sn)}>
                <ListItemIcon>
                  <Checkbox checked={assignedPlayerSns.includes(p.sn)} />
                </ListItemIcon>
                <ListItemText primary={p.sn} secondary={p.location} />
              </ListItem>
            ))}
            {playersList.length === 0 && (
              <ListItem>
                <ListItemText primary={t("advertiserAdmin.noPlayersForContract")} />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectPlayersOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveCampaignPlayers}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      {/* Select Ads Dialog */}
      <Dialog open={selectAdsOpen} onClose={() => setSelectAdsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("advertiserAdmin.selectAdsDialogTitle")}</DialogTitle>
        <DialogContent>
          <List>
            {availableAds.map((ad) => (
              <ListItem
                key={ad.id}
                dense
                button
                onClick={() => toggleAd(ad.id)}
                secondaryAction={
                  ad.file_name ? (
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => handleOpenAdPreview(ad.file_name!, e)}
                      title={t("advertiserAdmin.previewAd")}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  ) : null
                }
              >
                <ListItemIcon>
                  <Checkbox checked={adsSelection.some((a) => a.ad_id === ad.id)} />
                </ListItemIcon>
                <ListItemText
                  primary={ad.file_name ?? ad.name ?? String(ad.id)}
                  secondary={ad.duration != null ? `${Math.round(ad.duration / 1000)} s` : null}
                  primaryTypographyProps={{ sx: { fontSize: "0.875rem" } }}
                  secondaryTypographyProps={{ sx: { fontSize: "0.75rem", display: "block" } }}
                />
              </ListItem>
            ))}
            {availableAds.length === 0 && (
              <ListItem>
                <ListItemText primary={t("advertiserAdmin.noAdsForAdvertiser")} />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectAdsOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveCampaignAds}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      {/* Ad-Vorschau-Dialog */}
      <Dialog open={adPreviewOpen} onClose={handleCloseAdPreview} maxWidth="lg" fullWidth>
        <DialogTitle>{t("contentConfig.viewMedia")}</DialogTitle>
        <DialogContent>
          {adPreviewUrl && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              {adPreviewType === "video" && (
                <video
                  src={adPreviewUrl}
                  controls
                  style={{ width: "100%", maxHeight: "70vh", outline: "none" }}
                />
              )}
              {adPreviewType === "audio" && (
                <audio src={adPreviewUrl} controls style={{ width: "100%" }} />
              )}
              {adPreviewType === "image" && (
                <img src={adPreviewUrl} alt="" style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }} />
              )}
              {!adPreviewType && (
                <Typography color="text.secondary">{t("advertiserAdmin.previewAd")}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdPreview}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} message={snackbar} />
    </Box>
  );
};

export default AdvertiserAdminPanel;
